import { z } from "zod";
import type { ImageReference, ImagePanelDetails, PoseSheetAnalysis } from "./images";
import { imageSubjectTypes } from "./images";
import { zChoreo } from "./choreo";

/** Persist the SDK download URL verbatim, including its token and source environment. */
export const zImageUrl = z
  .string()
  .max(12000)
  .refine((value) => /^https?:\/\//i.test(value) || /^\/(?!\/)/.test(value), "Use an image download URL");
const finite = z.number().finite();
const box = z.object({ left: finite, top: finite, width: finite.nonnegative(), height: finite.nonnegative() });
export const zImagePanelDetails = z.object({
  row: finite.int().nonnegative(),
  column: finite.int().nonnegative(),
  disabled: z.boolean().optional(),
  sprite: box,
  frame: box,
  description: z.string().optional(),
  constraints: z.object({ top: z.boolean(), bottom: z.boolean(), left: z.boolean(), right: z.boolean() }).optional(),
  pose: z.custom<ImagePanelDetails["pose"]>((value) => typeof value === "string").optional(),
  orientation: z.enum(["left", "center", "right"]).optional(),
  relativeScale: finite.positive().optional(),
}) as z.ZodType<ImagePanelDetails>;
const grid = z.object({
  columns: finite.int().positive(),
  rows: finite.int().positive(),
  width: finite.positive(),
  height: finite.positive(),
  baselineSessionId: z.string().optional(),
});
const poseAnalysis = z.object({
  version: z.literal(1),
  status: z.enum(["pending", "analyzing", "ready", "failed"]),
  requestId: z.string(),
  imageId: z.string(),
  baselineImageId: z.string().optional(),
  baselinePanelId: z.string().optional(),
  requestedAt: finite.optional(),
  analyzedAt: finite.optional(),
  error: z.string().optional(),
  scaleReview: z.array(z.string()).optional(),
  orientationReview: z.array(z.string()).optional(),
  applied: z
    .object({
      relativeScale: finite.optional(),
      panels: z.record(
        z.object({
          description: z.string().optional(),
          orientation: z.enum(["left", "center", "right"]).optional(),
          relativeScale: finite.optional(),
        })
      ),
    })
    .optional(),
}) as z.ZodType<PoseSheetAnalysis>;

/** The complete ImageReference schema; use this for stored artwork, including snapshots. */
export const zImageReference = z.object({
  id: z.string().min(1).max(300),
  original: zImageUrl.optional(),
  large: zImageUrl.optional(),
  small: zImageUrl.optional(),
  svg: z.string().optional(),
  hash: z.string().optional(),
  prompt: z.string().optional(),
  error: z.string().optional(),
  errorCode: z.string().optional(),
  xOffset: finite.optional(),
  yOffset: finite.optional(),
  metadata: z
    .object({
      isUnsafe: z.boolean(),
      unsafeReason: z.string().optional(),
      isPhoto: z.boolean(),
      subjectType: z.enum(imageSubjectTypes),
      containsPerson: z.boolean(),
      dressedUpAs: z.string().optional(),
    })
    .optional(),
  resolution: z.enum(["1K", "2K"]).optional(),
  poseFraming: z.enum(["foreground", "full-character"]).optional(),
  poseAnalysis: poseAnalysis.optional(),
  relativeScale: finite.positive().optional(),
  purpose: z.literal("performance").optional(),
  posePlan: z.array(z.string()).optional(),
  choreos: z.array(zChoreo).optional(),
  panels: grid.extend({ details: z.record(zImagePanelDetails) }).optional(),
  selectedPanel: zImagePanelDetails.and(grid.extend({ name: z.string() })).optional(),
}) as z.ZodType<ImageReference>;
